import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Mail, Radar, Loader2, Save, Send, CheckCircle2, AlertCircle, RefreshCw, Zap, Palette, Upload, Trash2, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import MFASection from "@/components/security/MFASection";
import PasswordSection from "@/components/security/PasswordSection";
import SessionsSection from "@/components/security/SessionsSection";
import AdminAuditLogSection from "@/components/security/AdminAuditLogSection";
import AccessAnomaliesSection from "@/components/security/AccessAnomaliesSection";
import { usePermissions } from "@/lib/permissions";

export default function Settings() {
  const { isAdmin } = usePermissions();
  const [s, setS] = useState(null);
  const [email, setEmail] = useState({});
  const [shodan, setShodan] = useState({});
  const [savingEmail, setSavingEmail] = useState(false);
  const [savingShodan, setSavingShodan] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);
  const [sched, setSched] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(async () => {
    const r = await api.get("/settings");
    setS(r.data);
    setEmail({
      enabled: r.data.email.enabled,
      smtp_host: r.data.email.smtp_host || "",
      smtp_port: r.data.email.smtp_port || 587,
      smtp_username: r.data.email.smtp_username || "",
      smtp_password: "",
      from_email: r.data.email.from_email || "",
      recipients: (r.data.email.recipients || []).join(", "),
    });
    setShodan({
      enabled: r.data.shodan.enabled,
      api_key: "",
    });
  }, []);

  const loadSched = useCallback(async () => {
    try {
      const r = await api.get("/scans/shodan-status");
      setSched(r.data);
    } catch (err) {
      // Shodan status endpoint is optional (returns 404 if scheduler disabled).
      // Only surface unexpected failures — 404 is fine, everything else warns.
      if (err?.response?.status && err.response.status !== 404) {
        console.warn("Shodan scheduler status fetch failed:", err.response.status, err.message);
      }
    }
  }, []);

  useEffect(() => {
    load();
    loadSched();
    const t = setInterval(loadSched, 15000);
    return () => clearInterval(t);
  }, [load, loadSched]);

  const triggerSync = async () => {
    setSyncing(true);
    try {
      await api.post("/scans/shodan-sync");
      toast.success("Barrido en cola — ejecutándose en segundo plano");
      setTimeout(loadSched, 3000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo encolar");
    } finally { setSyncing(false); }
  };

  const saveEmail = async () => {
    setSavingEmail(true);
    try {
      const payload = {
        enabled: !!email.enabled,
        smtp_host: email.smtp_host || null,
        smtp_port: parseInt(email.smtp_port) || 587,
        smtp_username: email.smtp_username || null,
        smtp_password: email.smtp_password || null,
        from_email: email.from_email || null,
        recipients: (email.recipients || "").split(",").map(v => v.trim()).filter(Boolean),
      };
      await api.put("/settings/notifications", payload);
      toast.success("Configuración de correo guardada");
      setEmail(e => ({ ...e, smtp_password: "" }));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
    } finally { setSavingEmail(false); }
  };

  const saveShodan = async () => {
    setSavingShodan(true);
    try {
      await api.put("/settings/integrations/shodan", {
        enabled: !!shodan.enabled,
        api_key: shodan.api_key || null,
      });
      toast.success("Configuración de Shodan guardada");
      setShodan(s => ({ ...s, api_key: "" }));
      load();
    } catch (e) {
      toast.error("No se pudo guardar");
    } finally { setSavingShodan(false); }
  };

  const sendTest = async () => {
    if (!testTo) return;
    setTesting(true); setTestResult(null);
    try {
      const r = await api.post("/notifications/test", { to: testTo });
      setTestResult(r.data);
    } catch (e) {
      setTestResult({ dispatched: false, reason: "error", error: e?.response?.data?.detail || e.message });
    } finally { setTesting(false); }
  };

  if (!s) return <div className="p-8 font-mono text-sm text-muted-foreground">Loading settings...</div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="settings-page">
      <header>
        <div className="overline mb-2">Console · Configuration</div>
        <h1 className="font-display font-bold tracking-tighter" style={{ fontSize: "clamp(36px, 4vw, 56px)" }}>Settings</h1>
        <p className="text-muted-foreground text-sm mt-1">Notifications, integrations and platform behavior.</p>
      </header>

      {/* Cuenta propia — cambio de contraseña + MFA + sesiones (siempre visible) */}
      <PasswordSection />
      <MFASection />
      <SessionsSection />

      {isAdmin && (
        <>
          {/* Admin · Anomalías de acceso */}
          <AccessAnomaliesSection />

          {/* Admin · Auditoría de acceso */}
          <AdminAuditLogSection />
        </>
      )}

      {isAdmin && (
      <>
      {/* Email alerts */}
      <section className="border border-border bg-card/60 p-6" data-testid="settings-email">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-9 h-9 border border-primary/60 bg-primary/10 flex items-center justify-center"><Mail className="w-4 h-4 text-primary" /></div>
          <div className="flex-1">
            <div className="overline">Notifications</div>
            <h2 className="font-display text-2xl font-medium">Email alerts</h2>
            <p className="text-xs text-muted-foreground mt-1">
              Send alerts to a distribution list when critical findings, KEV matches, cert expirations or rating drops occur.
            </p>
          </div>
          <Toggle checked={email.enabled} onChange={(v) => setEmail(e => ({ ...e, enabled: v }))} testid="toggle-email" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="SMTP host">
            <input value={email.smtp_host} onChange={(e) => setEmail(x => ({ ...x, smtp_host: e.target.value }))}
                   placeholder="smtp.your-mail.com" data-testid="smtp-host"
                   className="w-full bg-background border border-border px-3 py-2 text-sm font-mono focus:border-primary outline-none" />
          </Field>
          <Field label="SMTP port">
            <input type="number" value={email.smtp_port} onChange={(e) => setEmail(x => ({ ...x, smtp_port: e.target.value }))}
                   data-testid="smtp-port"
                   className="w-full bg-background border border-border px-3 py-2 text-sm font-mono focus:border-primary outline-none" />
          </Field>
          <Field label="SMTP username">
            <input value={email.smtp_username} onChange={(e) => setEmail(x => ({ ...x, smtp_username: e.target.value }))}
                   data-testid="smtp-user"
                   className="w-full bg-background border border-border px-3 py-2 text-sm font-mono focus:border-primary outline-none" />
          </Field>
          <Field label={`SMTP password ${s.email.smtp_password_set ? "· (stored)" : ""}`}>
            <input type="password" value={email.smtp_password} onChange={(e) => setEmail(x => ({ ...x, smtp_password: e.target.value }))}
                   placeholder={s.email.smtp_password_set ? "leave blank to keep current" : "app password"}
                   data-testid="smtp-pass"
                   className="w-full bg-background border border-border px-3 py-2 text-sm font-mono focus:border-primary outline-none" />
          </Field>
          <Field label="From address">
            <input value={email.from_email} onChange={(e) => setEmail(x => ({ ...x, from_email: e.target.value }))}
                   placeholder="alerts@your-firm.com" data-testid="smtp-from"
                   className="w-full bg-background border border-border px-3 py-2 text-sm font-mono focus:border-primary outline-none" />
          </Field>
          <Field label="Recipients (comma-separated)">
            <input value={email.recipients} onChange={(e) => setEmail(x => ({ ...x, recipients: e.target.value }))}
                   placeholder="soc@client.com, ciso@client.com" data-testid="smtp-recipients"
                   className="w-full bg-background border border-border px-3 py-2 text-sm font-mono focus:border-primary outline-none" />
          </Field>
        </div>

        <div className="mt-4 flex flex-wrap justify-between items-center gap-3">
          <div className="flex items-center gap-2">
            <input value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="test recipient..."
                   className="bg-background border border-border px-3 py-2 text-sm font-mono focus:border-primary outline-none"
                   data-testid="test-to" />
            <button onClick={sendTest} disabled={testing || !testTo}
                    className="border border-primary/60 text-primary px-3 py-2 text-sm hover:bg-primary/10 disabled:opacity-50 inline-flex items-center gap-2"
                    data-testid="send-test">
              {testing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />} Send test
            </button>
          </div>
          <button onClick={saveEmail} disabled={savingEmail}
                  className="bg-primary text-primary-foreground px-4 py-2 text-sm hover:bg-primary/90 inline-flex items-center gap-2"
                  data-testid="save-email">
            {savingEmail ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save
          </button>
        </div>

        {testResult && (
          <div className={"mt-4 border p-3 text-xs font-mono flex items-start gap-2 " +
            (testResult.dispatched ? "border-sev-low/50 bg-sev-low/5 text-sev-low" : "border-sev-medium/50 bg-sev-medium/5 text-sev-medium")}
               data-testid="test-result">
            {testResult.dispatched ? <CheckCircle2 className="w-3 h-3 mt-0.5" /> : <AlertCircle className="w-3 h-3 mt-0.5" />}
            <div>
              <div>{testResult.dispatched ? "Email dispatched successfully." : `Not dispatched: ${testResult.reason}`}</div>
              {testResult.error && <div className="text-muted-foreground mt-1">{testResult.error}</div>}
              {testResult.would_send && (
                <div className="text-muted-foreground mt-1">
                  would send from {testResult.would_send.from || "—"} via {testResult.would_send.host || "—"}:{testResult.would_send.port}
                </div>
              )}
            </div>
          </div>
        )}
      </section>

      {/* Shodan */}
      <section className="border border-border bg-card/60 p-6" data-testid="settings-shodan">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-9 h-9 border border-primary/60 bg-primary/10 flex items-center justify-center"><Radar className="w-4 h-4 text-primary" /></div>
          <div className="flex-1">
            <div className="overline">Integrations</div>
            <h2 className="font-display text-2xl font-medium">Shodan · port scanning</h2>
            <p className="text-xs text-muted-foreground mt-1">
              Passive port and service intelligence for any monitored IP. Get an API key at{" "}
              <a href="https://account.shodan.io/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">account.shodan.io</a>.
            </p>
          </div>
          <Toggle checked={shodan.enabled} onChange={(v) => setShodan(x => ({ ...x, enabled: v }))} testid="toggle-shodan" />
        </div>

        <Field label={`API key ${s.shodan.api_key_set ? "· (stored)" : ""}`}>
          <input type="password" value={shodan.api_key} onChange={(e) => setShodan(x => ({ ...x, api_key: e.target.value }))}
                 placeholder={s.shodan.api_key_set ? "leave blank to keep current" : "SHODAN_XXXXXXXX"}
                 data-testid="shodan-key"
                 className="w-full bg-background border border-border px-3 py-2 text-sm font-mono focus:border-primary outline-none" />
        </Field>

        <div className="mt-4 flex justify-end">
          <button onClick={saveShodan} disabled={savingShodan}
                  className="bg-primary text-primary-foreground px-4 py-2 text-sm hover:bg-primary/90 inline-flex items-center gap-2"
                  data-testid="save-shodan">
            {savingShodan ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save
          </button>
        </div>

        <div className="mt-4 border border-border bg-background p-3 text-xs font-mono">
          <div className="overline mb-1">Status</div>
          <div className="flex items-center gap-2">
            <span className={"w-1.5 h-1.5 rounded-full " + (s.shodan.api_key_set ? "bg-sev-low" : "bg-sev-medium")} />
            {s.shodan.api_key_set ? "API key stored — live scans enabled." : "No key configured — scans return stub data."}
          </div>
        </div>

        {/* Sweep scheduler */}
        <div className="mt-4 border border-border bg-background p-4" data-testid="shodan-sweeps">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="overline mb-1">Scheduled sweeps</div>
              <div className="text-sm">Every {sched?.interval_seconds ? Math.round(sched.interval_seconds / 3600) : "6"}h</div>
              <div className="font-mono text-[10px] text-muted-foreground mt-1">
                Iterates every client&apos;s registered IPs, upserts findings, fires alert rules.
              </div>
            </div>
            <div className="text-right">
              <div className="flex items-center gap-2 justify-end">
                <span className={"w-1.5 h-1.5 rounded-full " + (sched?.running ? "bg-sev-low animate-pulse" : "bg-muted-foreground")} />
                <span className="font-mono text-[10px] tracking-widest uppercase">
                  {sched?.running ? "running" : "stopped"}
                </span>
              </div>
              <button
                onClick={triggerSync} disabled={syncing}
                className="mt-2 border border-primary/60 text-primary text-[10px] tracking-widest uppercase px-3 py-1.5 hover:bg-primary/10 disabled:opacity-50 inline-flex items-center gap-1"
                data-testid="run-sync-now"
              >
                {syncing ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                Run now
              </button>
            </div>
          </div>
          {sched?.last_run_at && (
            <div className="mt-3 pt-3 border-t border-border grid grid-cols-3 gap-2 font-mono text-[10px]">
              <div>
                <div className="overline">Last run</div>
                <div className="text-muted-foreground mt-0.5">{new Date(sched.last_run_at).toLocaleString()}</div>
              </div>
              <div>
                <div className="overline">Orgs scanned</div>
                <div className="mt-0.5">{sched.last_stats?.orgs_scanned ?? "—"}</div>
              </div>
              <div>
                <div className="overline">New findings</div>
                <div className="mt-0.5 text-sev-high">
                  {sched.last_stats?.results?.reduce((a, r) => a + (r.new_findings || 0), 0) ?? 0}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Alert rules shortcut */}
      <section className="border border-border bg-card/60 p-6">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 border border-primary/60 bg-primary/10 flex items-center justify-center"><Zap className="w-4 h-4 text-primary" /></div>
          <div className="flex-1">
            <div className="overline">Automation</div>
            <h2 className="font-display text-2xl font-medium">Alert rules</h2>
            <p className="text-xs text-muted-foreground mt-1">
              Send emails on new critical findings, KEV matches, rating drops, cert expirations and asset changes.
            </p>
          </div>
          <Link to="/alert-rules"
                className="border border-primary/60 text-primary text-xs tracking-widest uppercase px-3 py-2 hover:bg-primary/10 self-center inline-flex items-center gap-1"
                data-testid="link-alert-rules">
            Manage rules
          </Link>
        </div>
      </section>

      <BrandingSection settings={s} onSaved={load} />
      <DigestSection settings={s} onSaved={load} />
      </>
      )}
    </div>
  );
}

function BrandingSection({ settings, onSaved }) {
  const b = settings.branding || {};
  const [firmName, setFirmName] = useState(b.firm_name || "");
  const [subline, setSubline] = useState(b.subline || "");
  const [primaryColor, setPrimaryColor] = useState(b.primary_color || "#3B82F6");
  const [disclaimer, setDisclaimer] = useState(b.footer_disclaimer || "");
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoB64, setLogoB64] = useState(null);
  const [logoMime, setLogoMime] = useState(null);
  const [saving, setSaving] = useState(false);
  const [clearing, setClearing] = useState(false);

  useEffect(() => {
    setFirmName(b.firm_name || "");
    setSubline(b.subline || "");
    setPrimaryColor(b.primary_color || "#3B82F6");
    setDisclaimer(b.footer_disclaimer || "");
    setLogoPreview(null); setLogoB64(null); setLogoMime(null);
    // eslint-disable-next-line
  }, [settings]);

  const onLogo = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 512 * 1024) { toast.error("El logo debe ser < 512KB"); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      setLogoPreview(dataUrl);
      setLogoB64(dataUrl.split(",")[1]);
      setLogoMime(f.type);
    };
    reader.readAsDataURL(f);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        firm_name: firmName || null, subline: subline || null,
        primary_color: primaryColor, footer_disclaimer: disclaimer || null,
      };
      if (logoB64) { payload.logo_b64 = logoB64; payload.logo_mime = logoMime; }
      await api.put("/settings/branding", payload);
      toast.success("Marca guardada");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar la marca");
    } finally { setSaving(false); }
  };

  const clearLogo = async () => {
    setClearing(true);
    try {
      await api.put("/settings/branding", { clear_logo: true });
      toast.success("Logo eliminado");
      onSaved();
    } finally { setClearing(false); }
  };

  const logoUrl = b.logo_set
    ? `${process.env.REACT_APP_BACKEND_URL}/api/settings/branding/logo?t=${settings ? Object.keys(settings).length : 0}`
    : null;

  return (
    <section className="border border-border bg-card/60 p-6" data-testid="settings-branding">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-9 h-9 border border-primary/60 bg-primary/10 flex items-center justify-center"><Palette className="w-4 h-4 text-primary" /></div>
        <div className="flex-1">
          <div className="overline">Marca blanca</div>
          <h2 className="font-display text-2xl font-medium">Marca</h2>
          <p className="text-xs text-muted-foreground mt-1">
            Personaliza los informes PDF que reciben los clientes con el logo, nombre, color y pie legal de tu firma.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <label className="block">
          <div className="font-mono text-[10px] tracking-widest uppercase text-muted-foreground mb-1">Nombre de la firma</div>
          <input value={firmName} onChange={(e) => setFirmName(e.target.value)} maxLength={40}
                 placeholder="Acme Cyber Advisors" data-testid="brand-firm-name"
                 className="w-full bg-background border border-border px-3 py-2 text-sm font-mono focus:border-primary outline-none" />
        </label>
        <label className="block">
          <div className="font-mono text-[10px] tracking-widest uppercase text-muted-foreground mb-1">Subtítulo</div>
          <input value={subline} onChange={(e) => setSubline(e.target.value)} maxLength={80}
                 placeholder="INTELIGENCIA DE EXPOSICIÓN CIBERNÉTICA" data-testid="brand-subline"
                 className="w-full bg-background border border-border px-3 py-2 text-sm font-mono focus:border-primary outline-none" />
        </label>
        <label className="block">
          <div className="font-mono text-[10px] tracking-widest uppercase text-muted-foreground mb-1">Color principal</div>
          <div className="flex items-center gap-2">
            <input type="color" value={primaryColor} onChange={(e) => setPrimaryColor(e.target.value)} data-testid="brand-color-picker"
                   className="w-12 h-9 bg-background border border-border cursor-pointer" />
            <input value={primaryColor} onChange={(e) => setPrimaryColor(e.target.value)} data-testid="brand-color-hex"
                   className="flex-1 bg-background border border-border px-3 py-2 text-sm font-mono focus:border-primary outline-none" />
            <div className="w-9 h-9 border border-border" style={{ backgroundColor: primaryColor }} />
          </div>
        </label>
        <div>
          <div className="font-mono text-[10px] tracking-widest uppercase text-muted-foreground mb-1">Logo (PNG/JPG, ≤ 512KB)</div>
          <div className="flex items-center gap-3">
            <label className="border border-border px-3 py-2 hover:bg-muted cursor-pointer inline-flex items-center gap-2 text-xs font-mono flex-1" data-testid="brand-logo-upload">
              <Upload className="w-3 h-3" />
              <span className="truncate">{logoPreview ? "Cambiar archivo" : (b.logo_set ? "Reemplazar" : "Elegir archivo")}</span>
              <input type="file" accept="image/*" className="hidden" onChange={onLogo} />
            </label>
            {(logoPreview || b.logo_set) && (
              <div className="w-14 h-9 border border-border bg-background flex items-center justify-center overflow-hidden">
                <img src={logoPreview || logoUrl} alt="logo" className="max-w-full max-h-full" />
              </div>
            )}
            {b.logo_set && (
              <button onClick={clearLogo} disabled={clearing} className="border border-border p-2 hover:border-sev-critical hover:text-sev-critical" data-testid="brand-logo-clear" title="Eliminar logo">
                {clearing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
              </button>
            )}
          </div>
        </div>
      </div>
      <label className="block mt-4">
        <div className="font-mono text-[10px] tracking-widest uppercase text-muted-foreground mb-1">Aviso legal en el pie</div>
        <textarea value={disclaimer} onChange={(e) => setDisclaimer(e.target.value)} rows={3} maxLength={1000}
                  placeholder="Confidencial. Distribución restringida a destinatarios autorizados..." data-testid="brand-disclaimer"
                  className="w-full bg-background border border-border px-3 py-2 text-sm font-mono focus:border-primary outline-none resize-none" />
      </label>
      <div className="mt-4 flex justify-end">
        <button onClick={save} disabled={saving} data-testid="save-branding"
                className="bg-primary text-primary-foreground px-4 py-2 text-sm hover:bg-primary/90 disabled:opacity-50 inline-flex items-center gap-2">
          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Guardar marca
        </button>
      </div>
    </section>
  );
}

const DOWS = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"];

function DigestSection({ settings, onSaved }) {
  const d = settings.digest || {};
  const [enabled, setEnabled] = useState(!!d.enabled);
  const [dow, setDow] = useState(d.day_of_week ?? 0);
  const [hour, setHour] = useState(d.hour_utc ?? 9);
  const [recipients, setRecipients] = useState((d.recipients || []).join(", "));
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [lastRun, setLastRun] = useState(null);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    setEnabled(!!d.enabled); setDow(d.day_of_week ?? 0);
    setHour(d.hour_utc ?? 9); setRecipients((d.recipients || []).join(", "));
  }, [d.enabled, d.day_of_week, d.hour_utc, d.recipients]);

  useEffect(() => {
    api.get("/reports/digest/history?limit=10").then(r => setHistory(r.data)).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings/digest", {
        enabled, day_of_week: parseInt(dow), hour_utc: parseInt(hour),
        recipients: recipients.split(",").map(s => s.trim()).filter(Boolean),
      });
      toast.success("Horario del resumen guardado");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
    } finally { setSaving(false); }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const r = await api.post("/reports/digest/run");
      setLastRun(r.data);
      const sent = r.data.results?.filter(x => x.dispatched).length || 0;
      toast.success(`Resumen ejecutado — ${r.data.orgs_processed} clientes procesados · ${sent} enviados`);
      const h = await api.get("/reports/digest/history?limit=10");
      setHistory(h.data);
    } catch (e) {
      toast.error("Falló el resumen");
    } finally { setRunning(false); }
  };

  return (
    <section className="border border-border bg-card/60 p-6" data-testid="settings-digest">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-9 h-9 border border-primary/60 bg-primary/10 flex items-center justify-center"><CalendarClock className="w-4 h-4 text-primary" /></div>
        <div className="flex-1">
          <div className="overline">Automatización</div>
          <h2 className="font-display text-2xl font-medium">Resumen semanal del cliente</h2>
          <p className="text-xs text-muted-foreground mt-1">
            Genera automáticamente un mini PDF ejecutivo para cada cliente y lo envía por correo en el día y hora que elijas (UTC).
          </p>
        </div>
        <button type="button" onClick={() => setEnabled(!enabled)} data-testid="digest-toggle"
                className={"relative w-11 h-6 border transition-colors " + (enabled ? "bg-primary border-primary" : "bg-background border-border")}>
          <span className={"absolute top-0.5 w-4 h-4 bg-white transition-all " + (enabled ? "left-6" : "left-1")} />
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="block">
          <div className="font-mono text-[10px] tracking-widest uppercase text-muted-foreground mb-1">Día de la semana</div>
          <Select value={String(dow)} onValueChange={(v) => setDow(v)}>
            <SelectTrigger data-testid="digest-dow" className="w-full bg-background border border-border rounded-none text-sm font-mono focus:border-primary">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DOWS.map((n, i) => (
                <SelectItem key={n} value={String(i)} data-testid={`digest-dow-opt-${i}`}>{n}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="block">
          <div className="font-mono text-[10px] tracking-widest uppercase text-muted-foreground mb-1">Hora (UTC)</div>
          <Select value={String(hour)} onValueChange={(v) => setHour(v)}>
            <SelectTrigger data-testid="digest-hour" className="w-full bg-background border border-border rounded-none text-sm font-mono focus:border-primary">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[...Array(24).keys()].map((h) => (
                <SelectItem key={h} value={String(h)} data-testid={`digest-hour-opt-${h}`}>{String(h).padStart(2, "0")}:00</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <label className="block">
          <div className="font-mono text-[10px] tracking-widest uppercase text-muted-foreground mb-1">Destinatarios (predeterminado: tenant)</div>
          <input value={recipients} onChange={(e) => setRecipients(e.target.value)} data-testid="digest-recipients"
                 placeholder="ciso@cliente.com, soc@cliente.com"
                 className="w-full bg-background border border-border px-3 py-2 text-sm font-mono focus:border-primary outline-none" />
        </label>
      </div>

      <div className="mt-4 flex flex-wrap gap-2 justify-between items-center">
        <button onClick={runNow} disabled={running} data-testid="digest-run-now"
                className="border border-primary/60 text-primary text-xs tracking-widest uppercase px-3 py-2 hover:bg-primary/10 disabled:opacity-50 inline-flex items-center gap-2">
          {running ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />} Ejecutar resumen ahora (forzar)
        </button>
        <button onClick={save} disabled={saving} data-testid="save-digest"
                className="bg-primary text-primary-foreground px-4 py-2 text-sm hover:bg-primary/90 disabled:opacity-50 inline-flex items-center gap-2">
          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Guardar horario
        </button>
      </div>

      {lastRun && (
        <div className="mt-4 border border-border p-3 text-xs font-mono" data-testid="digest-last-run">
          <div className="overline mb-1">Última ejecución manual</div>
          <div>{lastRun.orgs_processed} clientes procesados · {(lastRun.results || []).filter(x => x.dispatched).length} enviados</div>
        </div>
      )}

      {history.length > 0 && (
        <div className="mt-4">
          <div className="overline mb-2">Historial reciente de resúmenes</div>
          <div className="space-y-1">
            {history.map(h => (
              <div key={h.id} className="flex items-center justify-between text-xs font-mono border border-border px-3 py-1.5">
                <span>{h.period_label} · <span className="text-muted-foreground">{h.iso_week}</span></span>
                <span className={h.dispatched ? "text-sev-low" : "text-sev-medium"}>
                  {h.dispatched ? "✓ enviado" : `⊘ ${h.reason || "no enviado"}`}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="font-mono text-[10px] tracking-widest uppercase text-muted-foreground mb-1">{label}</div>
      {children}
    </label>
  );
}

function Toggle({ checked, onChange, testid }) {
  return (
    <button type="button" onClick={() => onChange(!checked)}
            className={"relative w-11 h-6 border transition-colors " + (checked ? "bg-primary border-primary" : "bg-background border-border")}
            data-testid={testid}>
      <span className={"absolute top-0.5 w-4 h-4 bg-white transition-all " + (checked ? "left-6" : "left-1")} />
    </button>
  );
}
